/**
 * EditProfileView — Modifier mon profil
 * Allows editing: first_name, last_name, phone, address, birth_date, license_number, vtc_card_number.
 */
import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

export default function EditProfileView({ user, onBack }) {
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    birth_date: user?.birth_date || '',
    license_number: user?.license_number || '',
    vtc_card_number: user?.vtc_card_number || '',
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      await axios.put(`${API}/driver/profile`, form, { headers: getHeaders() });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'first_name', label: 'Prenom', type: 'text' },
    { key: 'last_name', label: 'Nom', type: 'text' },
    { key: 'phone', label: 'Telephone', type: 'tel' },
    { key: 'address', label: 'Adresse', type: 'text' },
    { key: 'birth_date', label: 'Date de naissance', type: 'date' },
    { key: 'license_number', label: 'N. permis de conduire', type: 'text' },
    { key: 'vtc_card_number', label: 'N. carte VTC', type: 'text' },
  ];

  return (
    <div className="space-y-3" data-testid="edit-profile-form">
      {/* Email (read-only) */}
      <div>
        <label className="text-white/40 text-xs mb-1 block">Email</label>
        <input
          type="email"
          value={user?.email || ''}
          disabled
          className="w-full bg-[#111] border border-white/5 rounded-lg px-3 py-2.5 text-white/30 text-sm cursor-not-allowed"
        />
      </div>

      {fields.map(f => (
        <div key={f.key}>
          <label className="text-white/40 text-xs mb-1 block">{f.label}</label>
          <input
            type={f.type}
            value={form[f.key]}
            onChange={e => handleChange(f.key, e.target.value)}
            className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#C9A24D]/50 focus:outline-none"
            data-testid={`edit-${f.key}`}
          />
        </div>
      ))}

      {error && <p className="text-red-400 text-xs">{error}</p>}
      {success && (
        <p className="text-emerald-400 text-xs flex items-center gap-1">
          <CheckCircle className="w-3.5 h-3.5" /> Profil mis a jour
        </p>
      )}

      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-black font-semibold min-h-[48px]"
        data-testid="save-profile-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
      </Button>
    </div>
  );
}
